import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useEditorData, useEditorHistory } from '../lib/FabricJs';
import { getSlideBounds } from '../utils/objectGeometry';
import { editorTheme } from '../theme';

export const RULER_SIZE = 20;

// Smallest labelled step (in slide pixels) that keeps labels this far apart.
const MIN_LABEL_SPACING = 50;
const MIN_TICK_SPACING = 4;
const STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

const REDRAW_EVENTS = [
  'selection:created',
  'selection:updated',
  'selection:cleared',
  'object:moving',
  'object:scaling',
  'object:rotating',
  'object:modified',
];

const RulerCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
`;

export const RulerCorner = styled.div`
  background: ${editorTheme.surface};
  border-right: 1px solid ${editorTheme.border};
  border-bottom: 1px solid ${editorTheme.border};
`;

const getWorkspace = () => document.getElementById('canvas-editor-container');

const pickStep = (zoom: number) =>
  STEPS.find((step) => step * zoom >= MIN_LABEL_SPACING) ??
  STEPS[STEPS.length - 1];

const draw = (
  element: HTMLCanvasElement,
  canvas: fabric.Canvas,
  horizontal: boolean,
) => {
  const ctx = element.getContext('2d');
  const canvasElement = canvas.getElement();
  if (!ctx || !canvasElement || !canvas.getContext()) return;
  const rect = element.getBoundingClientRect();
  const pixelRatio = window.devicePixelRatio || 1;
  element.width = Math.round(rect.width * pixelRatio);
  element.height = Math.round(rect.height * pixelRatio);
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

  const length = horizontal ? rect.width : rect.height;
  const thickness = horizontal ? rect.height : rect.width;
  const zoom = canvas.getZoom() || 1;
  const slideRect = canvasElement.getBoundingClientRect();
  // Screen position (in the ruler) of the slide's 0 and its far edge.
  const origin = horizontal
    ? slideRect.left - rect.left
    : slideRect.top - rect.top;
  const slideEnd = origin + (horizontal ? slideRect.width : slideRect.height);

  ctx.fillStyle = editorTheme.surfaceMuted;
  ctx.fillRect(
    0,
    0,
    horizontal ? length : thickness,
    horizontal ? thickness : length,
  );
  ctx.fillStyle = editorTheme.surface;
  const visibleStart = Math.max(0, origin);
  const visibleEnd = Math.min(length, slideEnd);
  if (visibleEnd > visibleStart) {
    if (horizontal) {
      ctx.fillRect(visibleStart, 0, visibleEnd - visibleStart, thickness);
    } else {
      ctx.fillRect(0, visibleStart, thickness, visibleEnd - visibleStart);
    }
  }

  const active = canvas.getActiveObject();
  if (active) {
    const bounds = getSlideBounds(active);
    const start = origin + (horizontal ? bounds.left : bounds.top) * zoom;
    const size = (horizontal ? bounds.width : bounds.height) * zoom;
    ctx.fillStyle = 'rgba(115, 0, 230, 0.16)';
    if (horizontal) ctx.fillRect(start, 0, size, thickness);
    else ctx.fillRect(0, start, thickness, size);
  }

  const step = pickStep(zoom);
  const minor = step >= 5 ? step / 5 : step;
  const drawMinor = minor * zoom >= MIN_TICK_SPACING;
  const tickUnit = drawMinor ? minor : step;
  const first = Math.floor(-origin / zoom / tickUnit) * tickUnit;

  ctx.strokeStyle = editorTheme.textMuted;
  ctx.fillStyle = editorTheme.textMuted;
  ctx.lineWidth = 1;
  ctx.font = '9px sans-serif';
  ctx.textBaseline = 'top';
  ctx.beginPath();
  for (let value = first; ; value += tickUnit) {
    const position = Math.round(origin + value * zoom) + 0.5;
    if (position > length) break;
    const isMajor = Math.abs(value % step) < 1e-6;
    const tick = isMajor ? thickness * 0.6 : thickness * 0.25;
    if (horizontal) {
      ctx.moveTo(position, thickness);
      ctx.lineTo(position, thickness - tick);
    } else {
      ctx.moveTo(thickness, position);
      ctx.lineTo(thickness - tick, position);
    }
    if (isMajor) {
      const label = String(Math.round(value));
      if (horizontal) {
        ctx.fillText(label, position + 3, 2);
      } else {
        ctx.save();
        ctx.translate(2, position - 3);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }
    }
  }
  ctx.stroke();

  ctx.strokeStyle = editorTheme.border;
  ctx.beginPath();
  if (horizontal) {
    ctx.moveTo(0, thickness - 0.5);
    ctx.lineTo(length, thickness - 0.5);
  } else {
    ctx.moveTo(thickness - 0.5, 0);
    ctx.lineTo(thickness - 0.5, length);
  }
  ctx.stroke();
};

// Shows slide pixels along the workspace edge, following zoom and scroll,
// with the selected object's extent highlighted.
export const Ruler: FC<{ orientation: 'horizontal' | 'vertical' }> = ({
  orientation,
}) => {
  const { canvas, appliedZoom } = useEditorData();
  const { history } = useEditorHistory();
  const ref = useRef<HTMLCanvasElement>(null);
  const frame = useRef<number>();
  const horizontal = orientation === 'horizontal';

  const schedule = () => {
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      frame.current = undefined;
      if (ref.current && canvas) draw(ref.current, canvas, horizontal);
    });
  };
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;

  useEffect(() => {
    const element = ref.current;
    if (!canvas || !element) return;
    const redraw = () => scheduleRef.current();
    const observer = new ResizeObserver(redraw);
    observer.observe(element);
    observer.observe(canvas.getElement());
    const workspace = getWorkspace();
    workspace?.addEventListener('scroll', redraw);
    REDRAW_EVENTS.forEach((name) => canvas.on(name, redraw));
    redraw();
    return () => {
      observer.disconnect();
      workspace?.removeEventListener('scroll', redraw);
      REDRAW_EVENTS.forEach((name) => canvas.off(name, redraw));
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [canvas]);

  useEffect(() => scheduleRef.current(), [appliedZoom, history.present]);

  return <RulerCanvas ref={ref} aria-hidden="true" />;
};
